/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * Services: Staggered card grid with thick borders. Monospace labels.
 * Cards lift on hover with dramatic shadow changes.
 */
import Reveal from "./Reveal";
import { Bot, Route, BarChart3, Phone, Wrench, Brain } from "lucide-react";

const services = [
  {
    icon: Bot,
    title: "AI Chatbots & Automation",
    description:
      "Custom AI assistants that handle customer calls, schedule appointments, and manage dispatch — 24/7, no breaks, no overtime.",
    tag: "AUTOMATION",
  },
  {
    icon: Route,
    title: "Smart Dispatch & Routing",
    description:
      "AI-powered route optimization that cuts fuel costs, reduces response times, and gets your trucks where they need to be — faster.",
    tag: "LOGISTICS",
  },
  {
    icon: BarChart3,
    title: "Business Intelligence",
    description:
      "Turn your operational data into actionable insights. See what's working, what's not, and where the money is going — in real time.",
    tag: "ANALYTICS",
  },
  {
    icon: Phone,
    title: "Customer Experience AI",
    description:
      "Automated follow-ups, review management, and customer communication that keeps your reputation strong and your phone ringing.",
    tag: "CRM",
  },
  {
    icon: Wrench,
    title: "Operations Optimization",
    description:
      "Streamline scheduling, inventory, and workforce management with AI tools built specifically for hands-on businesses.",
    tag: "OPERATIONS",
  },
  {
    icon: Brain,
    title: "AI Strategy Consulting",
    description:
      "Not sure where to start? We assess your business, identify the highest-impact AI opportunities, and build a roadmap that makes sense.",
    tag: "STRATEGY",
  },
];

export default function Services() {
  return (
    <section id="services" className="py-24 md:py-32 relative">
      {/* Diagonal top divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric/30 to-transparent" />

      <div className="container">
        {/* Section Header */}
        <div className="mb-16">
          <Reveal
            as="span"
            from="none"
            className="font-mono text-xs text-ember tracking-[0.3em] uppercase block mb-4"
          >
            // WHAT WE DO
          </Reveal>
          <Reveal
            as="h2"
            from="up"
            duration={0.6}
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4"
          >
            AI Solutions Built for
            <br />
            <span className="text-electric">the Real World</span>
          </Reveal>
          <Reveal
            as="p"
            from="up"
            duration={0.6}
            delay={0.1}
            className="text-steel-light text-lg max-w-2xl"
          >
            We don't sell generic software. We build AI tools tailored to how blue collar
            businesses actually operate — on the road, in the shop, and on the clock.
          </Reveal>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, i) => (
            <Reveal
              key={i}
              from="up"
              duration={0.5}
              delay={i * 0.1}
              className="group relative p-6 md:p-8 border-[3px] border-border bg-card hover:border-electric/50 transition-all duration-300 hover:-translate-y-1"
            >
              {/* Tag */}
              <span className="font-mono text-[10px] text-steel tracking-[0.2em] uppercase">
                {service.tag}
              </span>

              {/* Icon */}
              <div className="mt-4 mb-5 w-12 h-12 border-[2px] border-electric/30 bg-electric/10 flex items-center justify-center group-hover:bg-electric/20 transition-colors">
                <service.icon className="w-6 h-6 text-electric" />
              </div>

              {/* Content */}
              <h3 className="font-heading font-bold text-lg text-white mb-3">
                {service.title}
              </h3>
              <p className="text-steel-light text-sm leading-relaxed">
                {service.description}
              </p>

              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-8 h-8 border-b-[3px] border-l-[3px] border-electric/20 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
